import {
  cloneDeep
} from 'lodash';
import {
  IntentInfo,
  CompositeInfo,
  EntityInfo,
  DiffStates,
  DiffStatesClass
} from '@interface/index';
import { DataNode } from 'antd/lib/tree';
import { SYSTEM_ORG_NAME } from '@config/index';
import { ParameterMapStore } from './parameter-map';

export function getFullName(data: any): string {
  const ret = data.name;
  let prefix = '';

  if (data.qualification && data.qualification.length) {
    prefix = data.qualification;
  }

  if (data.source && data.source.length) {
    const source = data.source.replace('system.', '');
    if (prefix.length) {
      prefix = `${source}.${prefix}`;
    } else {
      prefix = source;
    }
  }

  if (data.originKey && data.originKey.agentId === data.name) {
    return prefix;
  }

  if (prefix.length) {
    return `${prefix}.${ret}`;
  }

  return ret;
}

export function getSimpleName(data: any): string {
  const ret = data.name;
  let prefix = '';

  if (data.qualification && data.qualification.length) {
    prefix = data.qualification;
  }

  if (prefix.length) {
    return `${prefix}.${ret}`;
  }

  return ret;
}


export function getPackageName(data: any): string {

  return data.source && data.source.replace('system.', '') || '';
}

export function getFormattedName(data: any): string {
  return `${getSimpleName(data)}${getPackageName(data) ? `(${getPackageName(data)})` : ''}`;
}

export function getFormattedNameTypeWithFullname(fullName: string): string {
  if (fullName.includes('(')) {
    return fullName;
  }

  const simpleName = fullName.split('.')[fullName.split('.').length - 1];
  let packageName = fullName.split('.').slice(0, fullName.split('.').length - 1).join('.');
  if (packageName) {
    packageName = packageName.replace('system.', '');
  }

  return `${simpleName}${packageName ? `(${packageName})` : ''}`;
}

export function getSimpleTypeName(fullName: string): string {
  return fullName.split('.').pop() || '';
}

export function availableStateFilter(item: any) {
  return item.state === DiffStates.ADD ||
    item.state === DiffStates.NORMAL ||
    item.state === DiffStates.PRIVATE_CONFLICT;
}

export function masterStateFilter(item: any) {
  return item.state === DiffStates.MASTER_CONFLICT;
}

export function delStateFilter(item: any) {
  return item.state === DiffStates.DEL;
}

export type ListLeafData = IntentInfo | CompositeInfo | EntityInfo;

export type ListItem = {
  key: string;
  isDir: boolean;
  name: string;
  subItems?: ListItem[];
  data?: ListLeafData;
  className?: string;
};

export function restructureList(list: ListLeafData[]): ListItem[] {
  const temp = cloneDeep(list).sort((item1: ListLeafData, item2: ListLeafData) => {
    if (item1.source === item2.source) {
      if (item1.qualification === item2.qualification) {
        if (!item1.name) {
          return 1;
        }

        if (!item2.name) {
          return -1;
        }

        return item1.name < item2.name ? -1 : 1;
      }

      if (!item1.qualification || !item1.qualification.length) {
        return 1;
      }

      if (!item2.qualification || !item2.qualification.length) {
        return -1;
      }

      return item1.qualification < item2.qualification ? -1 : 1;
    }

    if (!item1.source || !item1.source.length) {
      return 1;
    }

    if (!item2.source || !item2.source.length) {
      return -1;
    }

    return item1.source < item2.source ? 1 : -1;
  });

  const result: ListItem[] = [];

  const addDataToResult = (path: string[], data: ListLeafData) => {
    let tmpDir: ListItem = {
      key: '',
      isDir: true,
      name: 'root',
      subItems: result
    };

    for (const component of path) {

      const name = component.startsWith('#')
        ? component.substring(1, component.length)
        : component;
      const key = `${tmpDir.key}${tmpDir.key.length ? '-' : ''}${component}`;

      const foundItem = tmpDir.subItems && tmpDir.subItems.find((item: ListItem) =>
        item.isDir && item.key === key);

      if (!!foundItem) {
        tmpDir = foundItem;
      } else {
        const newItem: ListItem = {
          name,
          key,
          isDir: true,
          subItems: []
        };

        if (tmpDir.subItems) {
          tmpDir.subItems.push(newItem);
        }
        tmpDir = newItem;
      }
    }

    if (tmpDir.subItems) {
      tmpDir.subItems.push({
        data,
        key: `${path.join('-')}-${data.id || data.name}`,
        isDir: false,
        name: data.name || '',
        className: DiffStatesClass[data.state || '']
      });
    }
  };

  for (const item of temp) {
    const path: string[] = [];
    if (item.source && item.source.length) {
      item.source.split('.').forEach((component: string, index: number) => {
        if (!(component === SYSTEM_ORG_NAME && index === 0)) {
          path.push(`#${component}`);
        }
      });
    }

    if (item.qualification && item.qualification.length) {
      item.qualification.split('.').forEach((component: string) => {
        path.push(component);
      });
    }

    addDataToResult(path, item);
  }

  return result;
}

function convertTreeData(tree: ListItem[]): DataNode[] {

  return tree.map((item: ListItem) => ({
    title: item.name,
    key: item.key,
    children: item.subItems && convertTreeData(item.subItems),
    isLeaf: !item.isDir,
    leafData: item.data,
    className: item.className
  }));
}

export function generateTreeData(list: ListLeafData[]): DataNode[] {
  const newStruct = restructureList(list);
  return convertTreeData(newStruct);
}

export function showSource(source: string) {
  const sysPrefix = `${SYSTEM_ORG_NAME}.`;

  if (source.startsWith(sysPrefix)) {
    return source.replace(sysPrefix, '');
  }

  return source;
}
